import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Send, X, RotateCw, Sparkles, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "em_chat_session_v2";
const HISTORY_KEY = "em_chat_history_v2";

const getOrCreateSessionId = () => {
  let id = localStorage.getItem(STORAGE_KEY);
  if (!id) {
    id =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(STORAGE_KEY, id);
  }
  return id;
};

const SUGGESTIONS = [
  "How do I start?",
  "How much does the Starter tier cost?",
  "What is GrowthOS?",
  "Can you build a custom Web App?",
  "What platforms do you use for automation?",
  "What are your social media management offers?",
];

const WELCOME = {
  role: "assistant",
  content:
    "Hi — I'm **MCM**, an AI Concierge developed by Entrepminds. Ask me about our services, pricing, how to book a free diagnostic, or about our GrowthOS™.",
};

const MD_PARA = ({ children }) => (
  <p className="m-0 [&:not(:first-child)]:mt-2">{children}</p>
);
const MD_UL = ({ children }) => (
  <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>
);
const MD_OL = ({ children }) => (
  <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>
);
const MD_CODE = ({ children }) => (
  <code className="font-mono-em text-[12px] bg-black/5 px-1.5 py-0.5 rounded">
    {children}
  </code>
);
const userMarkdownComponents = {
  p: MD_PARA,
  ul: MD_UL,
  ol: MD_OL,
  code: MD_CODE,
  strong: ({ children }) => <strong className="text-[#d4af37]">{children}</strong>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline text-[#d4af37]"
    >
      {children}
    </a>
  ),
};
const assistantMarkdownComponents = {
  p: MD_PARA,
  ul: MD_UL,
  ol: MD_OL,
  code: MD_CODE,
  strong: ({ children }) => <strong className="text-[#8a6a10]">{children}</strong>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline text-[#8a6a10]"
    >
      {children}
    </a>
  ),
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) return arr;
      }
    } catch {
      /* ignore corrupt history */
    }
    return [WELCOME];
  });
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [unseen, setUnseen] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-40)));
    } catch {
      /* storage full or disabled */
    }
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || streaming) return;
      setMessages((prev) => [
        ...prev,
        { role: "user", content: trimmed },
        { role: "assistant", content: "", streaming: true },
      ]);
      setInput("");
      setStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetch(`${API}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId, message: trimmed }),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let assistantText = "";

        const applyEvent = (line) => {
          if (!line.startsWith("data:")) return;
          const json = line.slice(5).trim();
          if (!json) return;
          let evt;
          try {
            evt = JSON.parse(json);
          } catch (_) {
            return;
          }
          if (evt.type === "delta" && typeof evt.content === "string") {
            assistantText += evt.content;
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === "assistant" && last.streaming) {
                next[next.length - 1] = { ...last, content: assistantText };
              }
              return next;
            });
            if (!open) setUnseen(true);
          } else if (evt.type === "error") {
            assistantText = assistantText
              ? `${assistantText}\n\n_Error: ${evt.message}_`
              : `_${evt.message}_`;
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === "assistant") {
                next[next.length - 1] = { ...last, content: assistantText };
              }
              return next;
            });
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buffer.indexOf("\n\n")) >= 0) {
            const chunk = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            chunk.split("\n").forEach(applyEvent);
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.role === "assistant" && last.streaming) {
              next[next.length - 1] = {
                ...last,
                content:
                  last.content ||
                  "Sorry — I'm having trouble reaching the server. Please try again.",
              };
            }
            return next;
          });
        }
      } finally {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === "assistant" && last.streaming) {
            next[next.length - 1] = { ...last, streaming: false };
          }
          return next;
        });
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [sessionId, streaming, open]
  );

  const handleOpen = () => {
    setOpen(true);
    setUnseen(false);
  };

  const handleReset = async () => {
    if (streaming && abortRef.current) abortRef.current.abort();
    try {
      await fetch(`${API}/chat/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
    } catch {
      /* best-effort reset */
    }
    setMessages([WELCOME]);
    localStorage.removeItem(HISTORY_KEY);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            data-testid="chat-launcher-btn"
            onClick={handleOpen}
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: 20 }}
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            aria-label="Open AI assistant"
            className="fixed bottom-24 right-4 sm:right-6 z-[80] group"
          >
            <span className="absolute inset-0 rounded-full bg-[#d4af37] opacity-40 blur-xl group-hover:opacity-70 transition-opacity" />
            <span className="relative flex items-center gap-2 pl-4 pr-5 py-3.5 rounded-full bg-[#0a0a0a] text-[#f4e9c4] shadow-[0_18px_45px_-12px_rgba(0,0,0,0.45)] border border-[#d4af37]/40">
              <span className="relative">
                <Bot size={22} className="text-[#d4af37]" />
                {unseen && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#d4af37] ring-2 ring-[#0a0a0a]" />
                )}
              </span>
              <span className="font-mono-em text-[11px] tracking-[0.2em] uppercase">
                AI Concierge
              </span>
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[80] w-[min(92vw,380px)] h-[min(72vh,560px)] flex flex-col rounded-2xl overflow-hidden bg-white border border-[#d4af37]/40 shadow-[0_30px_70px_-20px_rgba(0,0,0,0.35)]"
          >
            <header className="relative px-4 py-3 sm:px-5 sm:py-4 bg-[#0a0a0a] text-white flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/45 flex items-center justify-center">
                  <Sparkles size={16} className="text-[#d4af37]" />
                </div>
                <div className="leading-tight">
                  <div className="font-display text-sm sm:text-base">MCM · AI Concierge</div>
                  <div className="font-mono-em text-[9px] sm:text-[10px] tracking-[0.2em] uppercase text-[#d4af37]">
                    Online · GrowthOS™
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="chat-reset-btn"
                  onClick={handleReset}
                  className="p-2 rounded-full text-[#a1a1aa] hover:text-[#d4af37] hover:bg-white/5 transition"
                  aria-label="Reset conversation"
                  title="Reset"
                >
                  <RotateCw size={16} />
                </button>
                <button
                  data-testid="chat-close-btn"
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-full text-[#a1a1aa] hover:text-[#d4af37] hover:bg-white/5 transition"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </header>

            <div
              ref={scrollRef}
              data-testid="chat-messages"
              className="flex-1 overflow-y-auto px-3.5 py-4 sm:px-4 sm:py-5 space-y-3.5 sm:space-y-4 bg-[#faf7ee]"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-msg-${m.role}-${i}`}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[88%] px-4 py-2.5 text-[13.5px] leading-relaxed rounded-2xl ${
                      m.role === "user"
                        ? "bg-[#0a0a0a] text-[#f4e9c4] rounded-br-sm"
                        : "bg-white text-[#0a0a0a] border border-[#e8e2cf] rounded-bl-sm"
                    }`}
                  >
                    {m.content ? (
                      <div className="prose-em">
                        <ReactMarkdown
                          components={
                            m.role === "user"
                              ? userMarkdownComponents
                              : assistantMarkdownComponents
                          }
                        >
                          {m.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <Loader2 size={14} className="animate-spin text-[#8a6a10]" />
                    )}
                  </div>
                </div>
              ))}

              {messages.length <= 1 && !streaming && (
                <div className="pt-2">
                  <div className="font-mono-em text-[10px] tracking-[0.2em] uppercase text-[#8a6a10] mb-3">
                    Quick prompts
                  </div>
                  <div className="grid gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        data-testid={`chat-suggestion-${s.slice(0, 20)}`}
                        onClick={() => sendMessage(s)}
                        className="text-left text-[12.5px] px-3.5 py-2 rounded-xl bg-white border border-[#e8e2cf] hover:border-[#d4af37]/60 hover:bg-[#fffdf6] text-[#3f3a30] transition"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex items-end gap-2 p-3 border-t border-[#e8e2cf] bg-white"
            >
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
                rows={1}
                placeholder="Ask about GrowthOS, pricing, services…"
                className="flex-1 resize-none bg-[#faf7ee] border border-[#e8e2cf] focus:border-[#d4af37] outline-none rounded-xl px-3.5 py-2.5 text-[13.5px] text-[#0a0a0a] placeholder:text-[#9a9180] transition max-h-32"
              />
              <button
                type="submit"
                data-testid="chat-send-btn"
                disabled={streaming || !input.trim()}
                aria-label="Send"
                className="shrink-0 w-11 h-11 rounded-xl bg-[#0a0a0a] text-[#f4e9c4] flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#b8901f] hover:text-[#0a0a0a] transition"
              >
                {streaming ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
